import {
  AddedDateFilter,
  AIClassificationFilter,
  AssetFilter,
  CopyrightImageFilter,
  CountryFilter,
  CustomTagsFilter,
  DateFilter,
  DomainFilter,
  FaviconFilter,
  GenericCustomTagFilter,
  InfringingImageFilter,
  LikelyCounterfeitFilter,
  LogoDetectedFilter,
  LowPriceFilter,
  ModelsFilter,
  MXRecordsFilter,
  PlatformsFilter,
  PostTypeFilter,
  PriceFilter,
  RepeatOffenderFilter,
  ReportedByUserFilter,
  ReportReasonFilter,
  RequestedDateFilter,
  SalesVolumeFilter,
  ScannedDateFilter,
  SellerFilter,
  ShipFromFilter,
  SmartRuleFilter,
  SSLCertificateFilter,
  TopResultsFilter,
  TrademarkLogoFilter,
  TrademarkTextFilter,
  ListingCommentFilter,
  DomainNamesCommentFilter,
  WebsiteContentCommentFilter,
  TestPurchaseProgressFilter,
  TestPurchaseReportFilter,
  WebsiteContentTestPurchaseProgressFilter,
} from '~/components/Filters';
import { FilterQueryParam } from '~/components/Filters/@types';
import {
  DomainNamesRouteEnum,
  MarketplaceListingsRouteEnum,
  SocialMediaListingsRouteEnum,
  WebsiteContentRouteEnum,
} from '~/router/@types';
import { ChannelGroup, FilterGroup, FilterGroups, FilterItem, FilterTypeEnum } from './@types';

// 필터 컴포넌트 매핑
export const FILTER_COMPONENTS = {
  [FilterTypeEnum.PLATFORMS]: PlatformsFilter,
  [FilterTypeEnum.SELLER]: SellerFilter,
  [FilterTypeEnum.ASSET]: AssetFilter,
  [FilterTypeEnum.MODELS]: ModelsFilter,
  [FilterTypeEnum.REPEAT_OFFENDER]: RepeatOffenderFilter,
  [FilterTypeEnum.TOP_RESULTS]: TopResultsFilter,
  [FilterTypeEnum.PRICE]: PriceFilter,
  [FilterTypeEnum.SALES_VOLUME]: SalesVolumeFilter,
  [FilterTypeEnum.SHIP_FROM]: ShipFromFilter,
  [FilterTypeEnum.CUSTOM_TAGS]: CustomTagsFilter,
  [FilterTypeEnum.LIKELY_COUNTERFEIT]: LikelyCounterfeitFilter,
  [FilterTypeEnum.LOW_PRICE]: LowPriceFilter,
  [FilterTypeEnum.REPORTED_BY_USER]: ReportedByUserFilter,
  [FilterTypeEnum.REPORT_REASON]: ReportReasonFilter,
  [FilterTypeEnum.SMART_RULE]: SmartRuleFilter,
  [FilterTypeEnum.DATE]: DateFilter,
  [FilterTypeEnum.COPYRIGHTED_IMAGE]: CopyrightImageFilter,
  [FilterTypeEnum.INFRINGING_IMAGE]: InfringingImageFilter,
  [FilterTypeEnum.TRADEMARKED_LOGO]: TrademarkLogoFilter,
  [FilterTypeEnum.TRADEMARKED_TEXT]: TrademarkTextFilter,
  [FilterTypeEnum.POST_TYPE]: PostTypeFilter,
  [FilterTypeEnum.COMMENT_USER_TYPE]: ListingCommentFilter,
  [FilterTypeEnum.TEST_PURCHASE_PROGRESS]: TestPurchaseProgressFilter,
  [FilterTypeEnum.TEST_PURCHASE_REPORT]: TestPurchaseReportFilter,
  // Domain Names & Website content
  [FilterTypeEnum.COUNTRY]: CountryFilter,
  [FilterTypeEnum.LOGO_DETECTED]: LogoDetectedFilter,
  [FilterTypeEnum.SCANNED_DATE]: ScannedDateFilter,
  [FilterTypeEnum.GENERIC_CUSTOM_TAGS]: GenericCustomTagFilter,
  [FilterTypeEnum.AI_CLASSIFICATION]: AIClassificationFilter,
  [FilterTypeEnum.FAVICON_DETECTED]: FaviconFilter,
  [FilterTypeEnum.ADDED_DATE]: AddedDateFilter,
  [FilterTypeEnum.REQUESTED_DATE]: RequestedDateFilter,
  [FilterTypeEnum.SSL_CERTIFICATE]: SSLCertificateFilter,
  [FilterTypeEnum.MX_RECORDS]: MXRecordsFilter,
  [FilterTypeEnum.DOMAIN]: DomainFilter,
  [FilterTypeEnum.DOMAIN_NAMES_COMMENT_USER_TYPE]: DomainNamesCommentFilter,
  [FilterTypeEnum.WEBSITE_CONTENT_COMMENT_USER_TYPE]: WebsiteContentCommentFilter,
  [FilterTypeEnum.WEBSITE_CONTENT_TEST_PURCHASE_PROGRESS]: WebsiteContentTestPurchaseProgressFilter,
} as const;

// 필터 타입과 쿼리 파라미터 매핑
export const FILTER_QUERY_PARAMS_MAP: Record<FilterTypeEnum, (keyof FilterQueryParam)[]> = {
  [FilterTypeEnum.PLATFORMS]: ['platforms'],
  [FilterTypeEnum.SELLER]: ['seller_search_keyword'],
  [FilterTypeEnum.ASSET]: ['assets'],
  [FilterTypeEnum.MODELS]: ['models'],
  [FilterTypeEnum.REPEAT_OFFENDER]: ['repeat_offender'],
  [FilterTypeEnum.TOP_RESULTS]: ['top_results'],
  [FilterTypeEnum.PRICE]: ['min_price', 'max_price'],
  [FilterTypeEnum.SALES_VOLUME]: ['min_sales_volume', 'max_sales_volume'],
  [FilterTypeEnum.SHIP_FROM]: ['ship_from'],
  [FilterTypeEnum.CUSTOM_TAGS]: ['custom_tag_id_filter'],
  [FilterTypeEnum.LIKELY_COUNTERFEIT]: ['likely_counterfeit'],
  [FilterTypeEnum.LOW_PRICE]: ['low_price'],
  [FilterTypeEnum.REPORTED_BY_USER]: ['requested'],
  [FilterTypeEnum.REPORT_REASON]: ['report_reason'],
  [FilterTypeEnum.SMART_RULE]: ['smart_rule_ids'],
  [FilterTypeEnum.DATE]: ['start_date', 'end_date'],
  [FilterTypeEnum.COPYRIGHTED_IMAGE]: ['ois'],
  [FilterTypeEnum.INFRINGING_IMAGE]: ['infringing_images_filter'],
  [FilterTypeEnum.TRADEMARKED_LOGO]: ['tm_logo', 'tm_logo_ids'],
  [FilterTypeEnum.TRADEMARKED_TEXT]: ['tm_text'],
  [FilterTypeEnum.POST_TYPE]: ['post_type_filter'],
  [FilterTypeEnum.COMMENT_USER_TYPE]: ['comment_user_type_filter'],
  [FilterTypeEnum.TEST_PURCHASE_PROGRESS]: ['tp_progress_status_filter'],
  [FilterTypeEnum.TEST_PURCHASE_REPORT]: ['tp_reported_filter'],
  // Domain Names & Website content
  [FilterTypeEnum.COUNTRY]: ['country_filter'],
  [FilterTypeEnum.LOGO_DETECTED]: ['logo_detection_filter'],
  [FilterTypeEnum.SCANNED_DATE]: ['scanned_date_start', 'scanned_date_end'],
  [FilterTypeEnum.GENERIC_CUSTOM_TAGS]: ['custom_tag_id_filter'],
  [FilterTypeEnum.AI_CLASSIFICATION]: ['domain_status_filter'],
  [FilterTypeEnum.FAVICON_DETECTED]: ['favicon_detected_filter'],
  [FilterTypeEnum.ADDED_DATE]: ['added_date_start', 'added_date_end'],
  [FilterTypeEnum.REQUESTED_DATE]: ['requested_date_start', 'requested_date_end'],
  [FilterTypeEnum.SSL_CERTIFICATE]: ['ssl_cert_filter'],
  [FilterTypeEnum.MX_RECORDS]: ['mx_records_filter'],
  [FilterTypeEnum.DOMAIN]: ['domain_id_filter'],
  [FilterTypeEnum.DOMAIN_NAMES_COMMENT_USER_TYPE]: ['comment_user_type_filter'],
  [FilterTypeEnum.WEBSITE_CONTENT_COMMENT_USER_TYPE]: ['comment_user_type_filter'],
  [FilterTypeEnum.WEBSITE_CONTENT_TEST_PURCHASE_PROGRESS]: ['tp_progress_status_filter'],
} as const;

export const FILTER_LABEL: Record<FilterTypeEnum, string> = {
  [FilterTypeEnum.PLATFORMS]: 'Platforms',
  [FilterTypeEnum.SELLER]: 'Sellers',
  [FilterTypeEnum.REPEAT_OFFENDER]: 'Repeat offender',
  [FilterTypeEnum.ASSET]: 'Assets',
  [FilterTypeEnum.MODELS]: 'Models',
  [FilterTypeEnum.SHIP_FROM]: 'Ship from',
  [FilterTypeEnum.CUSTOM_TAGS]: 'Tags',
  [FilterTypeEnum.LOW_PRICE]: 'Low price',
  [FilterTypeEnum.PRICE]: 'Price',
  [FilterTypeEnum.SALES_VOLUME]: 'Sales volume',
  [FilterTypeEnum.REPORT_REASON]: 'Report reason',
  [FilterTypeEnum.SMART_RULE]: 'Smart rules',
  [FilterTypeEnum.DATE]: 'Updated date',
  [FilterTypeEnum.LIKELY_COUNTERFEIT]: 'Likely counterfeit',
  [FilterTypeEnum.REPORTED_BY_USER]: 'Requested by me',
  [FilterTypeEnum.TOP_RESULTS]: 'Top search results',
  [FilterTypeEnum.COPYRIGHTED_IMAGE]: 'Copyrighted image used',
  [FilterTypeEnum.INFRINGING_IMAGE]: 'Infringing image used',
  [FilterTypeEnum.TRADEMARKED_LOGO]: 'Trademarked logo used',
  [FilterTypeEnum.TRADEMARKED_TEXT]: 'Trademarked text used',
  [FilterTypeEnum.POST_TYPE]: 'Post/Profile',
  [FilterTypeEnum.COUNTRY]: 'Country',
  [FilterTypeEnum.LOGO_DETECTED]: 'Logo detected',
  [FilterTypeEnum.SCANNED_DATE]: 'Scanned date',
  [FilterTypeEnum.GENERIC_CUSTOM_TAGS]: 'Tags',
  [FilterTypeEnum.AI_CLASSIFICATION]: 'AI Classification',
  [FilterTypeEnum.FAVICON_DETECTED]: 'Favicon detected',
  [FilterTypeEnum.ADDED_DATE]: 'Added date',
  [FilterTypeEnum.REQUESTED_DATE]: 'Requested date',
  [FilterTypeEnum.SSL_CERTIFICATE]: 'SSL certificate',
  [FilterTypeEnum.MX_RECORDS]: 'MX records',
  [FilterTypeEnum.DOMAIN]: 'Domain',
  [FilterTypeEnum.COMMENT_USER_TYPE]: 'Comments',
  [FilterTypeEnum.DOMAIN_NAMES_COMMENT_USER_TYPE]: 'Comments',
  [FilterTypeEnum.WEBSITE_CONTENT_COMMENT_USER_TYPE]: 'Comments',
  [FilterTypeEnum.TEST_PURCHASE_PROGRESS]: 'Test Purchase progress',
  [FilterTypeEnum.WEBSITE_CONTENT_TEST_PURCHASE_PROGRESS]: 'Test Purchase progress',
  [FilterTypeEnum.TEST_PURCHASE_REPORT]: 'Test Purchase report',
};

const MARKETPLACE_TP_FILTERS: Partial<Record<FilterGroup, FilterItem[]>> = {
  'Test Purchase case information': [
    { type: FilterTypeEnum.TEST_PURCHASE_PROGRESS, enabled: true },
    { type: FilterTypeEnum.TEST_PURCHASE_REPORT, enabled: true },
  ],
  'Platform and Seller Information': [
    { type: FilterTypeEnum.PLATFORMS, enabled: true },
    { type: FilterTypeEnum.SELLER, enabled: false },
  ],
  'Listing Attributes': [
    { type: FilterTypeEnum.ASSET, enabled: false },
    { type: FilterTypeEnum.MODELS, enabled: false },
    { type: FilterTypeEnum.SHIP_FROM, enabled: false },
    { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
  ],
};

const SOCIAL_MEDIA_TP_FILTERS: Partial<Record<FilterGroup, FilterItem[]>> = {
  'Test Purchase case information': [
    { type: FilterTypeEnum.TEST_PURCHASE_PROGRESS, enabled: true },
    { type: FilterTypeEnum.TEST_PURCHASE_REPORT, enabled: true },
  ],
  'Platform and Seller Information': [
    { type: FilterTypeEnum.PLATFORMS, enabled: true },
    { type: FilterTypeEnum.SELLER, enabled: false },
  ],
  'Listing Attributes': [
    { type: FilterTypeEnum.ASSET, enabled: false },
    { type: FilterTypeEnum.MODELS, enabled: false },
    { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
  ],
};

const MARKETPLACE_FILTERS: Record<MarketplaceListingsRouteEnum, FilterGroups> = {
  [MarketplaceListingsRouteEnum.Scanned]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
      { type: FilterTypeEnum.REPEAT_OFFENDER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.SHIP_FROM, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
    ],
    'Price and Sales': [
      { type: FilterTypeEnum.SALES_VOLUME, enabled: false },
      { type: FilterTypeEnum.PRICE, enabled: false },
    ],
    'Infringing features': [
      { type: FilterTypeEnum.COPYRIGHTED_IMAGE, enabled: false },
      { type: FilterTypeEnum.INFRINGING_IMAGE, enabled: false },
      { type: FilterTypeEnum.TRADEMARKED_LOGO, enabled: false },
      { type: FilterTypeEnum.TRADEMARKED_TEXT, enabled: false },
    ],
    Others: [{ type: FilterTypeEnum.TOP_RESULTS, enabled: false }],
  },
  [MarketplaceListingsRouteEnum.Flagged]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
      { type: FilterTypeEnum.REPEAT_OFFENDER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.SHIP_FROM, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
    ],
    'Price and Sales': [
      { type: FilterTypeEnum.SALES_VOLUME, enabled: false },
      { type: FilterTypeEnum.PRICE, enabled: false },
      { type: FilterTypeEnum.LOW_PRICE, enabled: false },
    ],
    'Infringing features': [
      { type: FilterTypeEnum.COPYRIGHTED_IMAGE, enabled: false },
      { type: FilterTypeEnum.INFRINGING_IMAGE, enabled: false },
      { type: FilterTypeEnum.TRADEMARKED_LOGO, enabled: false },
      { type: FilterTypeEnum.TRADEMARKED_TEXT, enabled: false },
    ],
    Others: [
      { type: FilterTypeEnum.LIKELY_COUNTERFEIT, enabled: false },
      { type: FilterTypeEnum.REPORTED_BY_USER, enabled: false },
      { type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false },
      { type: FilterTypeEnum.TOP_RESULTS, enabled: false },
    ],
  },

  // in-progress
  [MarketplaceListingsRouteEnum.Requested]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.SHIP_FROM, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
    ],
    Enforcement: [{ type: FilterTypeEnum.SMART_RULE, enabled: false }],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [
      { type: FilterTypeEnum.REPORTED_BY_USER, enabled: false },
      { type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [MarketplaceListingsRouteEnum.ReportSubmitted]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.SHIP_FROM, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
    ],
    Enforcement: [
      { type: FilterTypeEnum.REPORT_REASON, enabled: false },
      { type: FilterTypeEnum.SMART_RULE, enabled: false },
    ],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [
      { type: FilterTypeEnum.REPORTED_BY_USER, enabled: false },
      { type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [MarketplaceListingsRouteEnum.SoftNotice]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.SHIP_FROM, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
    ],
    Enforcement: [
      { type: FilterTypeEnum.REPORT_REASON, enabled: false },
      { type: FilterTypeEnum.SMART_RULE, enabled: false },
    ],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [
      { type: FilterTypeEnum.REPORTED_BY_USER, enabled: false },
      { type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [MarketplaceListingsRouteEnum.OnHold]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.SHIP_FROM, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
    ],
    Enforcement: [{ type: FilterTypeEnum.SMART_RULE, enabled: false }],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [
      { type: FilterTypeEnum.REPORTED_BY_USER, enabled: false },
      { type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false },
    ],
  },

  // case-closed
  removed: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.SHIP_FROM, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
    ],
    Enforcement: [
      { type: FilterTypeEnum.REPORT_REASON, enabled: false },
      { type: FilterTypeEnum.SMART_RULE, enabled: false },
    ],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [{ type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false }],
  },
  denied: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.SHIP_FROM, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
    ],
    Enforcement: [
      { type: FilterTypeEnum.REPORT_REASON, enabled: false },
      { type: FilterTypeEnum.SMART_RULE, enabled: false },
    ],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [{ type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false }],
  },
  'not-enforceable': {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.SHIP_FROM, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
    ],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [{ type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false }],
  },
  dismissed: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.SHIP_FROM, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
    ],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [{ type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false }],
  },
  [MarketplaceListingsRouteEnum.Archived]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.SHIP_FROM, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
    ],
    Enforcement: [{ type: FilterTypeEnum.SMART_RULE, enabled: false }],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [{ type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false }],
  },
  [MarketplaceListingsRouteEnum.TestPurchase]: MARKETPLACE_TP_FILTERS,
};

const SOCIAL_MEDIA_FILTERS: Record<
  SocialMediaListingsRouteEnum | string,
  Partial<Record<FilterGroup, FilterItem[]>>
> = {
  [SocialMediaListingsRouteEnum.Scanned]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
      { type: FilterTypeEnum.REPEAT_OFFENDER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.POST_TYPE, enabled: false },
    ],
    'Infringing features': [
      { type: FilterTypeEnum.COPYRIGHTED_IMAGE, enabled: false },
      { type: FilterTypeEnum.INFRINGING_IMAGE, enabled: false },
      { type: FilterTypeEnum.TRADEMARKED_LOGO, enabled: false },
      { type: FilterTypeEnum.TRADEMARKED_TEXT, enabled: false },
    ],
    Others: [{ type: FilterTypeEnum.TOP_RESULTS, enabled: false }],
  },
  [SocialMediaListingsRouteEnum.Flagged]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
      { type: FilterTypeEnum.REPEAT_OFFENDER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.POST_TYPE, enabled: false },
    ],
    'Infringing features': [
      { type: FilterTypeEnum.COPYRIGHTED_IMAGE, enabled: false },
      { type: FilterTypeEnum.INFRINGING_IMAGE, enabled: false },
      { type: FilterTypeEnum.TRADEMARKED_LOGO, enabled: false },
      { type: FilterTypeEnum.TRADEMARKED_TEXT, enabled: false },
    ],
    Others: [
      { type: FilterTypeEnum.LIKELY_COUNTERFEIT, enabled: false },
      { type: FilterTypeEnum.REPORTED_BY_USER, enabled: false },
      { type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false },
      { type: FilterTypeEnum.TOP_RESULTS, enabled: false },
    ],
  },

  // in-progress
  [SocialMediaListingsRouteEnum.Requested]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.POST_TYPE, enabled: false },
    ],
    Enforcement: [
      { type: FilterTypeEnum.REPORT_REASON, enabled: false },
      { type: FilterTypeEnum.SMART_RULE, enabled: false },
    ],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [
      { type: FilterTypeEnum.REPORTED_BY_USER, enabled: false },
      { type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [SocialMediaListingsRouteEnum.ReportSubmitted]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.POST_TYPE, enabled: false },
    ],
    Enforcement: [
      { type: FilterTypeEnum.REPORT_REASON, enabled: false },
      { type: FilterTypeEnum.SMART_RULE, enabled: false },
    ],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [
      { type: FilterTypeEnum.REPORTED_BY_USER, enabled: false },
      { type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [SocialMediaListingsRouteEnum.SoftNotice]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.POST_TYPE, enabled: false },
    ],
    Enforcement: [
      { type: FilterTypeEnum.REPORT_REASON, enabled: false },
      { type: FilterTypeEnum.SMART_RULE, enabled: false },
    ],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [
      { type: FilterTypeEnum.REPORTED_BY_USER, enabled: false },
      { type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [SocialMediaListingsRouteEnum.OnHold]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.POST_TYPE, enabled: false },
    ],
    Enforcement: [{ type: FilterTypeEnum.SMART_RULE, enabled: false }],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [
      { type: FilterTypeEnum.REPORTED_BY_USER, enabled: false },
      { type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false },
    ],
  },

  // case-closed
  [SocialMediaListingsRouteEnum.Removed]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.POST_TYPE, enabled: false },
    ],
    Enforcement: [
      { type: FilterTypeEnum.REPORT_REASON, enabled: false },
      { type: FilterTypeEnum.SMART_RULE, enabled: false },
    ],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [{ type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false }],
  },
  [SocialMediaListingsRouteEnum.Denied]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.POST_TYPE, enabled: false },
    ],
    Enforcement: [
      { type: FilterTypeEnum.REPORT_REASON, enabled: false },
      { type: FilterTypeEnum.SMART_RULE, enabled: false },
    ],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [{ type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false }],
  },
  [SocialMediaListingsRouteEnum.NotEnforceable]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.MODELS, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.POST_TYPE, enabled: false },
    ],
    Date: [{ type: FilterTypeEnum.DATE, enabled: false }],
    Others: [{ type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false }],
  },

  [SocialMediaListingsRouteEnum.Dismissed]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.POST_TYPE, enabled: false },
    ],
    Others: [{ type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false }],
  },

  [SocialMediaListingsRouteEnum.Archived]: {
    'Platform and Seller Information': [
      { type: FilterTypeEnum.PLATFORMS, enabled: true },
      { type: FilterTypeEnum.SELLER, enabled: false },
    ],
    'Listing Attributes': [
      { type: FilterTypeEnum.ASSET, enabled: false },
      { type: FilterTypeEnum.CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.POST_TYPE, enabled: false },
    ],
    Others: [{ type: FilterTypeEnum.COMMENT_USER_TYPE, enabled: false }],
  },

  [SocialMediaListingsRouteEnum.TestPurchase]: SOCIAL_MEDIA_TP_FILTERS,
};

const DOMAIN_NAMES_FILTERS: Record<DomainNamesRouteEnum, FilterGroups> = {
  [DomainNamesRouteEnum.Scanned]: {
    None: [
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
    ],
  },
  [DomainNamesRouteEnum.Flagged]: {
    None: [
      { type: FilterTypeEnum.AI_CLASSIFICATION, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.DOMAIN_NAMES_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [DomainNamesRouteEnum.Requested]: {
    None: [
      { type: FilterTypeEnum.AI_CLASSIFICATION, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.REQUESTED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.DOMAIN_NAMES_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [DomainNamesRouteEnum.Reported]: {
    None: [
      { type: FilterTypeEnum.AI_CLASSIFICATION, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.REQUESTED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.DOMAIN_NAMES_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [DomainNamesRouteEnum.Removed]: {
    None: [
      { type: FilterTypeEnum.AI_CLASSIFICATION, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.DOMAIN_NAMES_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [DomainNamesRouteEnum.Dismissed]: {
    None: [
      { type: FilterTypeEnum.AI_CLASSIFICATION, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.DOMAIN_NAMES_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [DomainNamesRouteEnum.Denied]: {
    None: [
      { type: FilterTypeEnum.AI_CLASSIFICATION, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.DOMAIN_NAMES_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [DomainNamesRouteEnum.NonEnforceable]: {
    None: [
      { type: FilterTypeEnum.AI_CLASSIFICATION, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.DOMAIN_NAMES_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [DomainNamesRouteEnum.Watchlist]: {
    None: [
      { type: FilterTypeEnum.AI_CLASSIFICATION, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.ADDED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.DOMAIN_NAMES_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [DomainNamesRouteEnum.Monitoring]: {},
};

const WEBSITE_CONTENT_FILTERS: Record<WebsiteContentRouteEnum, FilterGroups> = {
  [WebsiteContentRouteEnum.Scanned]: {
    None: [
      { type: FilterTypeEnum.DOMAIN, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
    ],
  },
  [WebsiteContentRouteEnum.Flagged]: {
    None: [
      { type: FilterTypeEnum.DOMAIN, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.WEBSITE_CONTENT_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [WebsiteContentRouteEnum.Requested]: {
    None: [
      { type: FilterTypeEnum.DOMAIN, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.REQUESTED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.WEBSITE_CONTENT_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [WebsiteContentRouteEnum.Reported]: {
    None: [
      { type: FilterTypeEnum.DOMAIN, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.REQUESTED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.WEBSITE_CONTENT_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [WebsiteContentRouteEnum.Removed]: {
    None: [
      { type: FilterTypeEnum.DOMAIN, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.REQUESTED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.WEBSITE_CONTENT_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [WebsiteContentRouteEnum.Denied]: {
    None: [
      { type: FilterTypeEnum.DOMAIN, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.REQUESTED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.WEBSITE_CONTENT_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [WebsiteContentRouteEnum.Dismissed]: {
    None: [
      { type: FilterTypeEnum.DOMAIN, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.SCANNED_DATE, enabled: false },
      { type: FilterTypeEnum.ADDED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
      { type: FilterTypeEnum.WEBSITE_CONTENT_COMMENT_USER_TYPE, enabled: false },
    ],
  },
  [WebsiteContentRouteEnum.TestPurchase]: {
    None: [
      { type: FilterTypeEnum.WEBSITE_CONTENT_TEST_PURCHASE_PROGRESS, enabled: true },
      { type: FilterTypeEnum.TEST_PURCHASE_REPORT, enabled: true },
      { type: FilterTypeEnum.DOMAIN, enabled: true },
      { type: FilterTypeEnum.COUNTRY, enabled: false },
      { type: FilterTypeEnum.LOGO_DETECTED, enabled: false },
      { type: FilterTypeEnum.FAVICON_DETECTED, enabled: false },
      { type: FilterTypeEnum.MX_RECORDS, enabled: false },
      { type: FilterTypeEnum.SSL_CERTIFICATE, enabled: false },
      { type: FilterTypeEnum.ADDED_DATE, enabled: false },
      { type: FilterTypeEnum.GENERIC_CUSTOM_TAGS, enabled: false },
    ],
  },
};

export const CHANNEL_FILTERS: Record<ChannelGroup, Record<string, FilterGroups>> = {
  marketplace: MARKETPLACE_FILTERS,
  'social media': SOCIAL_MEDIA_FILTERS,
  'domain names': DOMAIN_NAMES_FILTERS,
  'website content': WEBSITE_CONTENT_FILTERS,
};

export const FILTER_LOG_LABELS: Record<FilterTypeEnum, string> = {
  [FilterTypeEnum.PLATFORMS]: 'marketplaces(platform)',
  [FilterTypeEnum.SELLER]: 'sellers',
  [FilterTypeEnum.ASSET]: 'assets',
  [FilterTypeEnum.MODELS]: 'models',
  [FilterTypeEnum.TRADEMARKED_LOGO]: 'tm logo',
  [FilterTypeEnum.TRADEMARKED_TEXT]: 'tm text',
  [FilterTypeEnum.COPYRIGHTED_IMAGE]: 'copyrighted image',
  [FilterTypeEnum.INFRINGING_IMAGE]: 'infringing image',
  [FilterTypeEnum.REPEAT_OFFENDER]: 'repeat offender',
  [FilterTypeEnum.LOW_PRICE]: 'low price',
  [FilterTypeEnum.TOP_RESULTS]: 'top results',
  [FilterTypeEnum.REPORTED_BY_USER]: 'Reported by user(Requested by user)',
  [FilterTypeEnum.PRICE]: 'price',
  [FilterTypeEnum.SALES_VOLUME]: 'sales volume',
  [FilterTypeEnum.SHIP_FROM]: 'ship from',
  [FilterTypeEnum.CUSTOM_TAGS]: 'tags',
  [FilterTypeEnum.LIKELY_COUNTERFEIT]: 'likely counterfeit',
  [FilterTypeEnum.SMART_RULE]: 'smart rule',
  [FilterTypeEnum.DATE]: 'updated date',
  [FilterTypeEnum.REPORT_REASON]: 'report reason',
  [FilterTypeEnum.POST_TYPE]: 'post type',
  [FilterTypeEnum.COMMENT_USER_TYPE]: 'comments',
  // Domain Names & Website Content
  [FilterTypeEnum.COUNTRY]: 'country',
  [FilterTypeEnum.LOGO_DETECTED]: 'logo detected',
  [FilterTypeEnum.SCANNED_DATE]: 'scanned date',
  [FilterTypeEnum.GENERIC_CUSTOM_TAGS]: 'tags',
  [FilterTypeEnum.AI_CLASSIFICATION]: 'ai classification',
  [FilterTypeEnum.FAVICON_DETECTED]: 'favicon detected',
  [FilterTypeEnum.ADDED_DATE]: 'added date',
  [FilterTypeEnum.REQUESTED_DATE]: 'requested date',
  [FilterTypeEnum.SSL_CERTIFICATE]: 'ssl certificate',
  [FilterTypeEnum.MX_RECORDS]: 'mx records',
  [FilterTypeEnum.DOMAIN]: 'domain',
  [FilterTypeEnum.WEBSITE_CONTENT_COMMENT_USER_TYPE]: 'comments',
  [FilterTypeEnum.DOMAIN_NAMES_COMMENT_USER_TYPE]: 'comments',
  [FilterTypeEnum.TEST_PURCHASE_PROGRESS]: 'test purchase progress',
  [FilterTypeEnum.WEBSITE_CONTENT_TEST_PURCHASE_PROGRESS]: 'test purchase progress',
  [FilterTypeEnum.TEST_PURCHASE_REPORT]: 'test purchase report',
};
